import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '..'
import { OfrBlockType, OfrTemplateType } from '../../constants/constants'
import { HttpResponse, PrivacyType } from '../../types/generalTypes'

export interface OfrCell {
  isGhost?: boolean
  x1?: number
  y1?: number
  x2?: number
  y2?: number
}

export interface OcrText {
  level: number
  pageNum: number
  block: number
  paragraphNum: number
  lineNum: number
  wordNum: number
  top: number
  left: number
  width: number
  height: number
  confidence: number
  text: string
}

export interface WritingBlocks {
  id: number
  x: number
  y: number
  width: number
  height: number
}

export interface HtrText {
  text: string
  writingBlocks: WritingBlocks[]
  x1: number
  y1: number
  x2: number
  y2: number
}

export interface OfrBlockInfo {
  id: string
  ofrBlockName?: string
  ofrBlockType?: OfrBlockType
  cell?: OfrCell
  deviations?: number
  ocrTexts?: OcrText[]
  htrTexts?: HtrText[]
  isCheckbox?: boolean
  containsWriting?: boolean
  hasStrikeThrough?: boolean
  hasStrikeThroughText?: boolean
}

export interface OfrDocumentTemplatePage {
  id: string
  DPI: number
  writingHeightCM: number
  writingWidthCM: number
  imageWidth: number
  imageHeight: number
  blocks: OfrBlockInfo[]
}

export enum OfrStatusEnum {
  READY = 'READY',
  PROCESSING = 'PROCESSING',
  INGESTION = 'INGESTION',
  FAILED = 'FAILED',
}

type OFRStatusType = keyof typeof OfrStatusEnum

export interface OfrDocumentTemplate {
  id: string
  title: string
  formPages?: OfrDocumentTemplatePage[]
  description?: string | null
  dataSource: string
  documentId: string
  status: OFRStatusType
  createdBy?: string
  lastModifiedBy?: string
  createdDate?: string
  lastModifiedDate?: string
  archived?: boolean
  visibility?: PrivacyType
  templateType?: OfrTemplateType
  dataSourceTitle?: string
  documentName?: string
  uiFlags?: number
}

export interface OfrDocumentTemplateResponse extends HttpResponse {
  data: OfrDocumentTemplate[]
  uiFlags?: number
}

export interface OfrState {
  ofrDocumentTemplateList?: OfrDocumentTemplate[]
  selectedDocumentTemplate?: OfrDocumentTemplate
  selectedDocumentTemplateImages?: OFrImage[]
  ofrDocumentTemplateListCsv?: string
  loading: boolean
  ofrDocumentTemplateListTotalResults: number
  uiFlags?: number
}

const initialState: OfrState = {
  loading: false,
  selectedDocumentTemplate: undefined,
  ofrDocumentTemplateListTotalResults: 0,
}

export interface OfrFormImageBuffer {
  type: string
  data: number[]
}

export interface OFrImage {
  id: string
  stream: OfrFormImageBuffer
  image: string
}

const ofrDocumentTemplateSlice = createSlice({
  name: 'ofr',
  initialState,
  reducers: {
    setOfrDocumentTemplateList: (
      state,
      action: PayloadAction<OfrDocumentTemplate[]>
    ) => {
      state.ofrDocumentTemplateList = action.payload
    },

    setOfrLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setSelectedOfrDocumentTemplate: (
      state,
      action: PayloadAction<OfrDocumentTemplate>
    ) => {
      state.selectedDocumentTemplate = action.payload
    },
    setSelectedOfrDocumentTemplateImages: (
      state,
      action: PayloadAction<OFrImage[]>
    ) => {
      state.selectedDocumentTemplateImages = action.payload
    },
    setOfrDocumentTemplateListCsv: (state, action: PayloadAction<string>) => {
      state.ofrDocumentTemplateListCsv = action.payload
    },
    setOfrDocumentTemplateListTotalResults: (
      state,
      action: PayloadAction<number>
    ) => {
      state.ofrDocumentTemplateListTotalResults = action.payload
    },
    setOfrDocumentTemplateListUiFlags: (
      state,
      action: PayloadAction<number>
    ) => {
      state.uiFlags = action.payload
    },
    clearOfrState: () => initialState,
  },
})

export const {
  setOfrDocumentTemplateList,
  setOfrDocumentTemplateListCsv,
  setOfrLoading,
  setSelectedOfrDocumentTemplate,
  setSelectedOfrDocumentTemplateImages,
  setOfrDocumentTemplateListTotalResults,
  clearOfrState,
  setOfrDocumentTemplateListUiFlags,
} = ofrDocumentTemplateSlice.actions

export const selectedDocumentTemplate = (state: RootState) =>
  state.ofrDocumentTemplate.selectedDocumentTemplate

const ofrDocumentTemplateReducer = ofrDocumentTemplateSlice.reducer
export default ofrDocumentTemplateReducer
